import type { ReactNode } from 'react';
import { createContext, useContext, useState } from 'react';
import { useAppState } from '../../../lib/hooks/use-app-state.ts';
import { DialogStateManager, useDialog } from '../../dialog/dialog-provider.tsx';
import { Dialog } from '../../dialog/dialog.tsx';
import type { SelectedLineItemProduct } from '../../sales-draft/types/normal-line-machine-types.ts';
import { Catalog } from './catalog.tsx';

type CatalogDialogProps = {
    baseUrl: string;
    lastUrl: string;
    setLastUrl: (url: string) => void;
    onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void;
};

// The actual dialog component with iframe
const CatalogDialog = DialogStateManager.create(
    ({ baseUrl, lastUrl, onAddLineItem, setLastUrl }: CatalogDialogProps) => {
        const { visible, hide } = useDialog();

        return (
            <Dialog showClose open={visible} onClose={hide}>
                <Dialog.Content className="h-[calc(100vh-20px)] w-[calc(100vw-20px)] max-w-full overflow-hidden rounded-lg p-0">
                    <Catalog
                        baseUrl={baseUrl}
                        lastUrl={lastUrl}
                        setLastUrl={setLastUrl}
                        onAddLineItem={onAddLineItem}
                    />
                </Dialog.Content>
            </Dialog>
        );
    },
);

type CatalogContextType = {
    showCatalog: (
        onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void,
        draftId?: string,
    ) => Promise<void>;
};

const CatalogContext = createContext<CatalogContextType | undefined>(undefined);

export function CatalogProvider({ children }: { children: ReactNode }) {
    const { platoUser } = useAppState();
    const webshopUrl = platoUser?.platoCustomer.webshopUrl || '';
    const [lastUrl, setLastUrl] = useState<string | null>(null);

    const showCatalog = async (
        onAddLineItem: (product: SelectedLineItemProduct, quantity: number) => void,
    ): Promise<void> => {
        await DialogStateManager.show(CatalogDialog, {
            baseUrl: webshopUrl,
            lastUrl: lastUrl || webshopUrl,
            setLastUrl,
            onAddLineItem,
        });
    };

    return <CatalogContext.Provider value={{ showCatalog }}>{children}</CatalogContext.Provider>;
}

export function useCatalog() {
    const context = useContext(CatalogContext);
    if (!context) {
        throw new Error('useCatalog must be used within a CatalogProvider');
    }
    return context;
}
